import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material';
import {
  Edit,
  Refresh,
  CheckCircle,
  QuestionAnswer,
  Category,
  ArrowBack,
} from '@mui/icons-material';
import TemplateQuestionsEditor, { Question } from '../shared/TemplateQuestionsEditor';
import TemplateAIAssistPanel from '../shared/TemplateAIAssistPanel';

interface BuilderSession {
  session_id: string;
  status: string;
  template_preview: {
    template_name: string;
    icon_name: string;
    description?: string;
    total_questions: number;
    must_cover_themes?: string[];
    shortfall?: {
      target_question_count: number;
      generated_question_count: number;
      shortfall: number;
      suggested_themes?: string[];
      uncovered_must_cover?: string[];
    };
    structure: Array<{
      level01: string;
      level02?: string;
      level03?: string;
      questions: Array<{
        description: string;
        question_type: string;
        mandatory_beneficial: string;
        short_text?: string;
        long_text?: string;
        answer_options: Array<{
          option_text: string;
          option_seq: number;
          score?: number;
          action_item?: string;
        }>;
      }>;
    }>;
  };
}

interface TemplateReviewEditProps {
  session: BuilderSession;
  onApprove: (editedStructure: any) => void;
  onRegenerate: () => void;
  onBack: () => void;
  isLoading: boolean;
  error: string | null;
}

const TemplateReviewEdit: React.FC<TemplateReviewEditProps> = ({
  session,
  onApprove,
  onRegenerate,
  onBack,
  isLoading,
  error,
}) => {
  // Convert session structure to flat questions array for the editor
  const [questions, setQuestions] = useState<Question[]>([]);
  const [assistLocksEditor, setAssistLocksEditor] = useState(false);
  const [assistSeedPrompt, setAssistSeedPrompt] = useState('');
  const [assistSeedToken, setAssistSeedToken] = useState(0);
  const [templateDescription, setTemplateDescription] = useState<string>(
    session.template_preview.description || ''
  );

  const suggestedThemes =
    session.template_preview.shortfall?.suggested_themes ||
    session.template_preview.shortfall?.uncovered_must_cover ||
    [];

  const buildThemesPrompt = (themes: string[]) =>
    `Add distinct questions covering these missing themes (avoid overlap with existing questions):\n` +
    themes.map((t) => `- ${t}`).join('\n');

  const handleAddSuggestedThemes = () => {
    if (!suggestedThemes.length) return;
    setAssistSeedPrompt(buildThemesPrompt(suggestedThemes));
    setAssistSeedToken((n) => n + 1);
  };

  useEffect(() => {
    // Convert structure to questions on mount
    const flatQuestions: Question[] = [];
    let sequence = 1;
    
    session.template_preview.structure.forEach((section) => {
      section.questions.forEach((q) => {
        flatQuestions.push({
          id: Date.now() + sequence, // Generate temp ID
          sequence,
          level01: section.level01,
          level02: section.level02 || '',
          level03: section.level03 || '',
          level04: '',
          level05: '',
          description: q.description,
          mandatory_beneficial: q.mandatory_beneficial,
          question_type: q.question_type,
          short_text: q.short_text || '',
          long_text: q.long_text || '',
          options: q.answer_options.map((opt) => ({
            option_text: opt.option_text,
            option_seq: opt.option_seq,
            score: opt.score || 0,
            action_item: opt.action_item || '',
          })),
        });
        sequence++;
      });
    });
    
    setQuestions(flatQuestions);
  }, [session]);

  /**
   * Handle questions change from editor
   */
  const handleQuestionsChange = (updatedQuestions: Question[]) => {
    setQuestions(updatedQuestions);
  };

  /**
   * Convert flat questions back to structure format for backend
   */
  const handleContinue = () => {
    // Group questions by category to rebuild structure
    const structureMap = new Map<string, any>();
    
    questions.forEach((q, idx) => {
      const key = `${q.level01}|${q.level02}|${q.level03}`;
      
      if (!structureMap.has(key)) {
        structureMap.set(key, {
          level01: q.level01,
          level02: q.level02,
          level03: q.level03,
          questions: [],
        });
      }
      
      structureMap.get(key).questions.push({
        description: q.description,
        question_type: q.question_type,
        mandatory_beneficial: q.mandatory_beneficial,
        short_text: q.short_text,
        long_text: q.long_text,
        answer_options: q.options.map((opt) => ({
          option_text: opt.option_text,
          option_seq: opt.option_seq,
          // Preserve scores and action items when continuing to KPI step / save
          score: opt.score ?? 0,
          action_item: opt.action_item || '',
        })),
      });
    });
    
    const structure = Array.from(structureMap.values());
    
    onApprove({
      ...session.template_preview,
      description: templateDescription,
      structure,
      total_questions: questions.length,
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Edit color="primary" fontSize="large" />
        <Box>
          <Typography variant="h5" gutterBottom>
            Review & Edit Your AI-Generated Template
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review the template structure and questions. You can regenerate if you want different content, or continue to configure KPIs.
          </Typography>
        </Box>
      </Box>

      {/* Template Overview */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'success.dark', color: 'success.contrastText' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CheckCircle />
          <Typography variant="h6">
            ✨ Template Generated Successfully!
          </Typography>
        </Box>
        
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          {session.template_preview.template_name}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <Chip 
            icon={<QuestionAnswer />}
            label={`${questions.length} Questions`} 
            sx={{ bgcolor: 'white', color: 'success.main' }}
          />
          <Chip 
            icon={<Category />}
            label={`${new Set(questions.map(q => q.level01)).size} Categories`} 
            sx={{ bgcolor: 'white', color: 'success.main' }}
          />
          <Chip 
            label={`Icon: ${session.template_preview.icon_name}`} 
            sx={{ bgcolor: 'white', color: 'success.main' }}
          />
        </Box>
      </Paper>

      {/* Template Description */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit fontSize="small" />
          Template Description
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add a brief description that explains what this review template covers. This will be shown to users when browsing templates.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={templateDescription}
          onChange={(e) => setTemplateDescription(e.target.value)}
          placeholder="Example: This review template evaluates quality management systems against ISO 9001 standards, covering documentation, process controls, and continuous improvement practices."
          disabled={isLoading}
          variant="outlined"
        />
      </Paper>

      {((session.template_preview.shortfall?.shortfall || 0) > 0 ||
        (session.template_preview.shortfall?.uncovered_must_cover?.length || 0) > 0) && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              suggestedThemes.length > 0 ? (
                <Button color="inherit" size="small" onClick={handleAddSuggestedThemes} disabled={isLoading}>
                  Add suggested themes
                </Button>
              ) : undefined
            }
          >
            {session.template_preview.shortfall &&
            session.template_preview.shortfall.shortfall > 0 ? (
              <>
                Ceiling was {session.template_preview.shortfall.target_question_count}; generated{' '}
                {session.template_preview.shortfall.generated_question_count} distinct questions
                (short by {session.template_preview.shortfall.shortfall}). No duplicate padding.
              </>
            ) : (
              <>Some must-cover themes look thin or missing in the generated set.</>
            )}
            {suggestedThemes.length > 0 && (
              <>
                {' '}
                Suggested themes: {suggestedThemes.slice(0, 6).join('; ')}
                {suggestedThemes.length > 6 ? '…' : ''}.
              </>
            )}{' '}
            Use <strong>Add suggested themes</strong> to open AI Assist with a ready prompt, or continue with this set.
          </Alert>
        )}

      <Alert severity="info" sx={{ mb: 3 }}>
        Edit questions, answer options, scores, and action items. Questions are automatically grouped by category. Use the "Edit Question" dialog to change categories.
      </Alert>

      <TemplateAIAssistPanel
        questions={questions}
        onQuestionsApplied={handleQuestionsChange}
        suggestedThemes={suggestedThemes}
        disabled={isLoading}
        onProposalActiveChange={setAssistLocksEditor}
        seedPrompt={assistSeedPrompt}
        seedToken={assistSeedToken}
        autoRunOnSeed
      />

      {/* Shared Questions Editor */}
      <TemplateQuestionsEditor
        questions={questions}
        onChange={handleQuestionsChange}
        isReadOnly={assistLocksEditor}
        loading={isLoading}
      />

      <Divider sx={{ my: 3 }} />

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="text"
            size="small"
            onClick={onBack}
            disabled={isLoading}
            startIcon={<ArrowBack />}
          >
            Back to Requirements
          </Button>
          
          <Button
            variant="outlined"
            size="small"
            onClick={onRegenerate}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <Refresh />}
          >
            {isLoading ? 'Regenerating...' : 'Regenerate Template'}
          </Button>
        </Box>
        
        <Button
          variant="contained"
          size="small"
          onClick={handleContinue}
          disabled={isLoading || questions.length === 0}
          startIcon={<CheckCircle />}
          data-testid="template-review-next-button"
        >
          Next: Configure KPIs
        </Button>
      </Box>
    </Box>
  );
};

export default TemplateReviewEdit; 